import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { TabPanel, TabView } from 'primereact/tabview';
import { useContext, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ToastContext from '../../../../../context/ToastContext';
import useAxios from '../../../../../hooks/useAxios';
import useAxiosHook from '../../../../../hooks/useAxiosHook';
import { ErrorResponse } from '../../../../../types/api';
import { ClientResource } from '../../../../../types/api/clients';
import { tryApiContextValidationRHF } from '../../../../../utils/helpers/react-hook-form';
import {
  FormValues,
  NotificationDefault,
  allowedErrors,
  createDialogApiData,
  customerConfigsOnly,
  editDialogApiData,
  generateInvoicingCheckboxProperties,
  getDefaultValues,
  getValidationSchema,
  isHeadquarter,
  tabIcon,
} from './CreateEditDialog.functions';
import ChargingDepartmentsTab from './Tabs/ChargingDepartmentsTab';
import Discounts from './Tabs/Discounts/Discounts';
import EmailConfiguration from './Tabs/EmailConfiguration/EmailConfiguration';
import NotificationsTab from './Tabs/Fields/NotificationsTab/NotificationsTab';
import GeneralInfoTab from './Tabs/GeneralInfoTab';
import LegalDataTab from './Tabs/LegalDataTab';
import OrdersTab from './Tabs/OrdersTab';

type Props = {
  createEditDialogFormRef: any;
  isEditDialog: boolean;
  clientData: ClientResource | undefined;
  onLoadingChange?: (isLoading: boolean) => void;
  headquarterData: ClientResource | undefined;
  configsData?: any;
  reload: () => void;
  onHide: () => void;
  id?: string | undefined;
};

function FormClientContainer({
  createEditDialogFormRef,
  isEditDialog,
  clientData,
  onLoadingChange,
  headquarterData,
  configsData,
  reload,
  onHide,
  id,
}: Props) {
  const { t } = useTranslation();
  const { toastRef } = useContext(ToastContext);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const { reload: formSubmitRequest, isLoading: isFormSubmitRequestLoading } =
    useAxios();

  useEffect(() => {
    onLoadingChange?.(isFormSubmitRequestLoading);
  }, [isFormSubmitRequestLoading, onLoadingChange]);

  const defaultValues = useMemo(
    () => getDefaultValues(t, isEditDialog, clientData, configsData),
    [t, isEditDialog, configsData, clientData]
  );

  const validationSchema = useMemo(
    () => getValidationSchema(t, configsData, isEditDialog),
    [t, configsData, isEditDialog]
  );

  const methods = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    getValues,
    setError,
    watch,
    formState: { errors },
  } = methods;
  const values = watch();
  console.log('errors ', errors);
  const fieldSettings = customerConfigsOnly(configsData?.data);

  const { data: smsGateways } = useAxiosHook('/clients/sms/gateways');

  const { data: defaultNotificationsData } = useAxiosHook<
    NotificationDefault[]
  >('/notifications/default');

  const smsGatewayOptions = useMemo(
    () => [
      { label: t("Don't notify"), value: '' },
      { label: t('Default Provider'), value: 'default' },
      ...(Array.isArray(smsGateways) ? smsGateways : [])
        .filter((g) => typeof g === 'string')
        .map((g) => ({ label: g, value: g })),
    ],
    [smsGateways, t]
  );

  const defaultNotifications = useMemo(() => {
    if (!defaultNotificationsData) {
      return;
    }

    let returnObj: any = {};

    for (const n of defaultNotificationsData) {
      returnObj[`${n.event}-${n.actor}-${n.channel}`] = n;
    }

    return returnObj;
  }, [defaultNotificationsData]);

  const invoicingCheckboxProperties = useMemo(
    () =>
      generateInvoicingCheckboxProperties(
        {
          direkcija_id: values?.direkcija_id,
          broj_dogovor: values?.broj_dogovor,
          faktura: values?.faktura,
        },
        isHeadquarter(values?.pravno_lice, clientData, values?.direkcija_id),
        headquarterData,
        clientData,
        t
      ),
    [
      clientData,
      headquarterData,
      t,
      values?.pravno_lice,
      values?.direkcija_id,
      values?.broj_dogovor,
      values?.faktura,
    ]
  );

  useEffect(() => {
    setActiveTabIndex(0);

    return () => {
      setActiveTabIndex(0);
    };
  }, []);

  const generalInfoTabIcon = tabIcon(
    t,
    'general-info',
    getValues(),
    errors,
    isEditDialog
  );

  const legalDataTabIcon = tabIcon(
    t,
    'legal-data',
    getValues(),
    errors,
    isEditDialog
  );

  const discountsTabIcon = tabIcon(
    t,
    'discounts',
    getValues(),
    errors,
    isEditDialog
  );

  const ordersTabIcon = tabIcon(t, 'orders', getValues(), errors, isEditDialog);

  const notificationsTabIcon = tabIcon(
    t,
    'notifications',
    getValues(),
    errors,
    isEditDialog
  );

  const chargingDepartmentsTabIcon = tabIcon(
    t,
    'charging-departments',
    getValues(),
    errors,
    isEditDialog
  );

  const emailConfigurationTabIcon = tabIcon(
    t,
    'email-configuration',
    getValues(),
    errors,
    isEditDialog
  );

  function handleClientEdit(name: string, isSuccess: boolean) {
    toastRef?.current?.show({
      severity: isSuccess ? 'success' : 'error',
      summary: isSuccess ? t('Success') : t('Error'),
      detail: isSuccess
        ? t('Client {{name}} has been edited successfully', { name: name })
        : t('An error occured while editing {{name}}.', { name: name }),
      life: 5000,
    });
    if (isSuccess) onHide();
    reload();
  }

  function handleClientAdd(name: string, isSuccess: boolean) {
    toastRef?.current?.show({
      severity: isSuccess ? 'success' : 'error',
      summary: isSuccess ? t('Success') : t('Error'),
      detail: isSuccess
        ? t('Client {{name}} has been added successfully', { name: name })
        : t('An error occured while adding {{name}}.', { name: name }),
      life: 5000,
    });
    if (isSuccess) onHide();
    reload();
  }

  function handleSubmitForm(values: FormValues) {
    const allowedErrorss = allowedErrors(t);

    if (isEditDialog) {
      formSubmitRequest({
        url: `/clients/${id}`,
        payload: editDialogApiData(values, id!, defaultValues),
        method: 'PUT',
        successCallback: () => {
          handleClientEdit(values.ime, true);
        },
        errorCallback: (err: AxiosError<ErrorResponse>) => {
          const errorDescription = err.response?.data.error_description;

          if (
            errorDescription &&
            tryApiContextValidationRHF(
              errorDescription,
              setError,
              allowedErrorss
            )
          ) {
            return;
          }

          handleClientEdit(values.ime, false);
        },
      });
    } else {
      formSubmitRequest({
        url: '/clients',
        payload: createDialogApiData(values),
        method: 'POST',
        successCallback: () => {
          handleClientAdd(values.ime, true);
        },
        errorCallback: (err: AxiosError<ErrorResponse>) => {
          const errorDescription = err.response?.data.error_description;

          if (
            errorDescription &&
            tryApiContextValidationRHF(
              errorDescription,
              setError,
              allowedErrorss
            )
          ) {
            return;
          }

          handleClientAdd(values.ime, false);
        },
      });
    }
  }

  return (
    <FormProvider {...methods}>
      <form
        ref={createEditDialogFormRef}
        id="clients-create-edit-form"
        onSubmit={handleSubmit(handleSubmitForm)}
      >
        <TabView
          activeIndex={activeTabIndex}
          onTabChange={(e) => setActiveTabIndex(e.index)}
          className={Object.keys(errors).length > 0 ? 'invalid' : ''}
        >
          <TabPanel header={t('General info')} {...generalInfoTabIcon}>
            <GeneralInfoTab
              clientData={clientData}
              isEditDialog={isEditDialog}
            />
          </TabPanel>

          <TabPanel
            header={t('Legal data')}
            // headerStyle={values.pravno_lice ? {} : { display: 'none' }}
            headerClassName="data-cy-legal-data-tab"
            {...legalDataTabIcon}
          >
            <LegalDataTab
              clientData={clientData}
              isEditDialog={isEditDialog}
              invoicingCheckboxProperties={invoicingCheckboxProperties}
              fieldSettings={fieldSettings}
            />
          </TabPanel>

          <TabPanel
            header={t('Discounts')}
            headerStyle={values?.pravno_lice ? {} : { display: 'none' }}
            headerClassName="data-cy-discounts-data-tab"
            {...discountsTabIcon}
          >
            <Discounts disabled={!invoicingCheckboxProperties.checked} />
          </TabPanel>

          <TabPanel
            header={t('Orders')}
            headerClassName="data-cy-orders-tab"
            {...ordersTabIcon}
          >
            <OrdersTab />
          </TabPanel>

          <TabPanel
            header={t('Notifications')}
            headerClassName="data-cy-notifications-tab"
            {...notificationsTabIcon}
          >
            <NotificationsTab
              defaultNotifications={defaultNotifications}
              smsGatewayOptions={smsGatewayOptions}
            />
          </TabPanel>

          <TabPanel
            header={t('Charging Departments')}
            headerStyle={
              values?.pravno_lice &&
              ((isEditDialog && clientData?.id === values?.direkcija_id) ||
                (!isEditDialog && values?.direkcija_id === 0))
                ? {}
                : { display: 'none' }
            }
            headerClassName="data-cy-charging-departments"
            {...chargingDepartmentsTabIcon}
          >
            <ChargingDepartmentsTab
              clientData={clientData}
              isEditDialog={isEditDialog}
            />
          </TabPanel>

          <TabPanel
            header={t('Email Configuration')}
            {...emailConfigurationTabIcon}
          >
            <EmailConfiguration />
          </TabPanel>
        </TabView>
      </form>
    </FormProvider>
  );
}

export default FormClientContainer;
